import { useEffect, useState } from 'react'

import useCurrentUser, { useUpdateCurrentUser } from 'query/useCurrentUser'
import {
  Alert,
  Button,
  Field,
  Form,
  FormActions,
  FormCard,
  FormStack,
  Input,
  PageContent,
  PageHeader,
} from 'ui'

const getFullName = user => `${user.firstName || ''} ${user.lastName || ''}`.trim()

export const ProfilePageComponent = () => {
  const { data: currentUser = {}, setData: setCurrentUser } = useCurrentUser()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const updateProfileMutation = useUpdateCurrentUser({
    onError: error => {
      setEmail(currentUser.email || '')
      setErrorMessage(error.message || 'Failed to update profile. Please try again.')
    },
    onSuccess: response => {
      const nextCurrentUser = { ...currentUser, ...response }
      setCurrentUser(nextCurrentUser)
      setName(getFullName(nextCurrentUser))
      setEmail(nextCurrentUser.email || '')
      setSuccessMessage('Profile updated successfully')
      setPassword('')
      setConfirmPassword('')
    },
  })

  useEffect(() => {
    if (!currentUser.id && !currentUser.email && !currentUser.firstName && !currentUser.lastName && !currentUser.name) return
    setName(getFullName(currentUser))
    setEmail(currentUser.email || '')
  }, [currentUser])

  const handleSubmit = event => {
    event.preventDefault()
    setSuccessMessage('')
    setErrorMessage('')

    if (password || confirmPassword) {
      if (password !== confirmPassword) {
        setErrorMessage('Passwords do not match')
        return
      }

      if (password.length < 6) {
        setErrorMessage('Password must be at least 6 characters')
        return
      }
    }

    const payload = { name, email }
    if (password && currentUser.provider === 'password') payload.password = password
    updateProfileMutation.mutate(payload)
  }

  const isPasswordProvider = currentUser.provider === 'password'
  const resetForm = () => {
    setName(getFullName(currentUser))
    setEmail(currentUser.email || '')
    setPassword('')
    setConfirmPassword('')
    setSuccessMessage('')
    setErrorMessage('')
  }

  return (
    <>
      <PageHeader subtitle="Update your personal account details and password." title="Profile" />
      <PageContent>
        <Form className="tw:mx-auto tw:max-w-form" onSubmit={handleSubmit}>
          {(successMessage || errorMessage) && (
            <Alert color={errorMessage ? 'danger' : 'success'}>{errorMessage || successMessage}</Alert>
          )}
          <FormStack>
            <FormCard title="My Profile">
              <Field label="Name">
                <Input id="name" name="name" onChange={event => setName(event.target.value)} placeholder="Full name" value={name} />
              </Field>
              <Field label="Email">
                <Input id="email" name="email" onChange={event => setEmail(event.target.value)} placeholder="Email" type="email" value={email} />
              </Field>
            </FormCard>

            {isPasswordProvider && (
              <FormCard description="Leave both fields blank to keep your current password." title="Change Password">
                <Field helper="At least 6 characters." label="New Password">
                  <Input id="password" name="password" onChange={event => setPassword(event.target.value)} type="password" value={password} />
                </Field>
                <Field label="Confirm New Password">
                  <Input id="confirmPassword" name="confirmPassword" onChange={event => setConfirmPassword(event.target.value)} type="password" value={confirmPassword} />
                </Field>
              </FormCard>
            )}
          </FormStack>
          <FormActions className="tw:mt-5">
            <Button variant="secondary" onClick={resetForm} type="button">Cancel</Button>
            <Button disabled={updateProfileMutation.isPending} type="submit">
              {updateProfileMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </FormActions>
        </Form>
      </PageContent>
    </>
  )
}

export const ProfilePage = ProfilePageComponent
